// Primary 4 English Learning Data - Part 1 (Topics 1-10)
// Based on Primary English Learning P4 Topics.txt

export const p4DataPart1 = {
  gradeId: 'p4',
  gradeName: 'Primary 4 (小學四年級)',
  objectives: [
    'Build on Primary 3 foundations with more complex grammar structures',
    'Develop advanced reading comprehension and writing skills',
    'Learn future tense and complex verb forms',
    'Master idioms and verb collocations',
    'Understand complex sentence structures and determiners'
  ],
  keySkills: [
    'Question formation and complex structures',
    'Future tense and verb collocations',
    'Idioms and sequence markers',
    'Determiners and pronouns',
    'Advanced sentence patterns'
  ],
  topics: [
    {
      id: 'p4-topic-1',
      title: 'Question Formation',
      description: 'Learning to form different types of questions with proper word order',
      purpose: 'To teach students how to form various types of questions with correct word order and structure, including yes/no questions, wh-questions, and tag questions.',
      questions: [
        {
          question: 'Complete: "_____ is your name?"',
          answer: 'What',
          explanation: 'Use "What" to ask about things, names, or objects.'
        },
        {
          question: 'Fill in the blank: "_____ do you live?"',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location or place.'
        },
        {
          question: 'Complete: "_____ do you go to school?"',
          answer: 'When',
          explanation: 'Use "When" to ask about time or schedule.'
        },
        {
          question: 'Fill in the blank: "_____ is your best friend?"',
          answer: 'Who',
          explanation: 'Use "Who" to ask about people.'
        },
        {
          question: 'Complete: "_____ do you like ice cream?"',
          answer: 'Why',
          explanation: 'Use "Why" to ask for reasons or explanations.'
        },
        {
          question: 'Fill in the blank: "_____ old are you?"',
          answer: 'How',
          explanation: 'Use "How" to ask about quantity, age, or manner.'
        },
        {
          question: 'Complete: "_____ is your school?"',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location.'
        },
        {
          question: 'Fill in the blank: "_____ do you eat breakfast?"',
          answer: 'When',
          explanation: 'Use "When" to ask about time.'
        },
        {
          question: 'Complete: "_____ is your favorite subject?"',
          answer: 'What',
          explanation: 'Use "What" to ask about preferences or choices.'
        },
        {
          question: 'Fill in the blank: "_____ do you go to school?"',
          answer: 'How',
          explanation: 'Use "How" to ask about the way or method.'
        }
      ]
    },
    {
      id: 'p4-topic-2',
      title: 'Future Tense',
      description: 'Learning to use future tense with "will" and "going to"',
      purpose: 'To teach students how to form and use future tense to express plans, predictions, and intentions using "will" and "going to".',
      questions: [
        {
          question: 'I _____ go to school tomorrow.',
          answer: 'will',
          explanation: 'Use "will" to express future actions or predictions.'
        },
        {
          question: 'She _____ visit her grandmother next week.',
          answer: 'will',
          explanation: 'Use "will" to express future plans or intentions.'
        },
        {
          question: 'They _____ play football this weekend.',
          answer: 'will',
          explanation: 'Use "will" to express future activities.'
        },
        {
          question: 'I _____ going to study tonight.',
          answer: 'am',
          explanation: 'Use "am going to" to express planned future actions.'
        },
        {
          question: 'He _____ going to buy a new car.',
          answer: 'is',
          explanation: 'Use "is going to" to express planned future actions.'
        },
        {
          question: 'We _____ going to have a party.',
          answer: 'are',
          explanation: 'Use "are going to" to express planned future actions.'
        },
        {
          question: 'It _____ rain tomorrow.',
          answer: 'will',
          explanation: 'Use "will" to express predictions about weather.'
        },
        {
          question: 'I _____ going to cook dinner.',
          answer: 'am',
          explanation: 'Use "am going to" to express planned future actions.'
        },
        {
          question: 'She _____ going to learn English.',
          answer: 'is',
          explanation: 'Use "is going to" to express planned future actions.'
        },
        {
          question: 'They _____ going to travel to Japan.',
          answer: 'are',
          explanation: 'Use "are going to" to express planned future actions.'
        }
      ]
    },
    {
      id: 'p4-topic-3',
      title: 'Verb Collocations',
      description: 'Learning to use common verb + noun combinations',
      purpose: 'To teach students how to use common verb + noun combinations that frequently occur together in English.',
      questions: [
        {
          question: 'I _____ a shower every morning.',
          answer: 'take',
          explanation: 'Use "take" with "shower" - this is a common collocation.'
        },
        {
          question: 'She _____ a photo of the sunset.',
          answer: 'takes',
          explanation: 'Use "take" with "photo" - this is a common collocation.'
        },
        {
          question: 'He _____ a decision quickly.',
          answer: 'makes',
          explanation: 'Use "make" with "decision" - this is a common collocation.'
        },
        {
          question: 'We _____ a mistake in the test.',
          answer: 'made',
          explanation: 'Use "make" with "mistake" - this is a common collocation.'
        },
        {
          question: 'I _____ my homework before dinner.',
          answer: 'do',
          explanation: 'Use "do" with "homework" - this is a common collocation.'
        },
        {
          question: 'She _____ the dishes after eating.',
          answer: 'washes',
          explanation: 'Use "wash" with "dishes" - this is a common collocation.'
        },
        {
          question: 'He _____ a phone call to his friend.',
          answer: 'makes',
          explanation: 'Use "make" with "phone call" - this is a common collocation.'
        },
        {
          question: 'I _____ a break after studying.',
          answer: 'take',
          explanation: 'Use "take" with "break" - this is a common collocation.'
        },
        {
          question: 'She _____ a picture of her family.',
          answer: 'draws',
          explanation: 'Use "draw" with "picture" - this is a common collocation.'
        },
        {
          question: 'We _____ a plan for the weekend.',
          answer: 'make',
          explanation: 'Use "make" with "plan" - this is a common collocation.'
        }
      ]
    },
    {
      id: 'p4-topic-4',
      title: 'Idioms',
      description: 'Learning to understand and use common English idioms',
      purpose: 'To teach students common English idioms and their meanings to improve comprehension and expression.',
      questions: [
        {
          question: 'What does "break a leg" mean?',
          answer: 'good luck',
          explanation: '"Break a leg" is an idiom meaning "good luck", often used before performances.'
        },
        {
          question: 'What does "piece of cake" mean?',
          answer: 'very easy',
          explanation: '"Piece of cake" is an idiom meaning something is very easy to do.'
        },
        {
          question: 'What does "hit the books" mean?',
          answer: 'study hard',
          explanation: '"Hit the books" is an idiom meaning to study hard.'
        },
        {
          question: 'What does "cost an arm and a leg" mean?',
          answer: 'very expensive',
          explanation: '"Cost an arm and a leg" is an idiom meaning something is very expensive.'
        },
        {
          question: 'What does "let the cat out of the bag" mean?',
          answer: 'reveal a secret',
          explanation: '"Let the cat out of the bag" is an idiom meaning to reveal a secret.'
        },
        {
          question: 'What does "pull someone\'s leg" mean?',
          answer: 'joke with someone',
          explanation: '"Pull someone\'s leg" is an idiom meaning to joke with someone.'
        },
        {
          question: 'What does "hit the nail on the head" mean?',
          answer: 'be exactly right',
          explanation: '"Hit the nail on the head" is an idiom meaning to be exactly right.'
        },
        {
          question: 'What does "break the ice" mean?',
          answer: 'start a conversation',
          explanation: '"Break the ice" is an idiom meaning to start a conversation in a social situation.'
        },
        {
          question: 'What does "barking up the wrong tree" mean?',
          answer: 'looking in the wrong place',
          explanation: '"Barking up the wrong tree" is an idiom meaning to look in the wrong place for something.'
        },
        {
          question: 'What does "get out of hand" mean?',
          answer: 'become uncontrollable',
          explanation: '"Get out of hand" is an idiom meaning to become uncontrollable or chaotic.'
        }
      ]
    },
    {
      id: 'p4-topic-5',
      title: 'Determiners',
      description: 'Learning to use determiners (this, that, these, those, some, any, many, much)',
      purpose: 'To teach students how to use determiners to specify or quantify nouns in different contexts.',
      questions: [
        {
          question: '_____ book is mine.',
          answer: 'This',
          explanation: 'Use "This" as a demonstrative determiner before a singular noun that is near.'
        },
        {
          question: '_____ are my friends.',
          answer: 'These',
          explanation: 'Use "These" as a demonstrative determiner for plural things that are near.'
        },
        {
          question: '_____ car is expensive.',
          answer: 'That',
          explanation: 'Use "That" as a demonstrative determiner before a singular noun that is far.'
        },
        {
          question: '_____ are flowers.',
          answer: 'Those',
          explanation: 'Use "Those" as a demonstrative determiner for plural things that are far.'
        },
        {
          question: 'I have _____ apples.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with countable nouns.'
        },
        {
          question: 'Do you have _____ water?',
          answer: 'any',
          explanation: 'Use "any" for questions and negative statements with uncountable nouns.'
        },
        {
          question: 'There are _____ books on the shelf.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ money.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        },
        {
          question: 'I have _____ friends.',
          answer: 'a few',
          explanation: 'Use "a few" with countable nouns to show a small quantity.'
        },
        {
          question: 'There is _____ sugar in the bowl.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with uncountable nouns.'
        }
      ]
    },
    {
      id: 'p4-topic-6',
      title: 'Present Perfect Tense',
      description: 'Learning to use present perfect tense to describe past actions with present relevance',
      purpose: 'To teach students how to form and use present perfect tense to describe actions that happened in the past but have relevance to the present.',
      questions: [
        {
          question: 'I _____ never been to Japan.',
          answer: 'have',
          explanation: 'Use "have" with "I" in present perfect tense.'
        },
        {
          question: 'She _____ finished her homework.',
          answer: 'has',
          explanation: 'Use "has" with third person singular in present perfect tense.'
        },
        {
          question: 'They _____ lived here for five years.',
          answer: 'have',
          explanation: 'Use "have" with plural subjects in present perfect tense.'
        },
        {
          question: 'He _____ eaten breakfast already.',
          answer: 'has',
          explanation: 'Use "has" with third person singular in present perfect tense.'
        },
        {
          question: 'We _____ studied English for three years.',
          answer: 'have',
          explanation: 'Use "have" with "we" in present perfect tense.'
        },
        {
          question: 'The movie _____ started.',
          answer: 'has',
          explanation: 'Use "has" with third person singular in present perfect tense.'
        },
        {
          question: 'I _____ seen that movie before.',
          answer: 'have',
          explanation: 'Use "have" with "I" in present perfect tense.'
        },
        {
          question: 'She _____ written a letter.',
          answer: 'has',
          explanation: 'Use "has" with third person singular in present perfect tense.'
        },
        {
          question: 'They _____ arrived at the station.',
          answer: 'have',
          explanation: 'Use "have" with plural subjects in present perfect tense.'
        },
        {
          question: 'He _____ broken his leg.',
          answer: 'has',
          explanation: 'Use "has" with third person singular in present perfect tense.'
        }
      ]
    },
    {
      id: 'p4-topic-7',
      title: 'Adverbs of Manner',
      description: 'Learning to use adverbs of manner to describe how actions are performed',
      purpose: 'To teach students how to use adverbs of manner to describe the way in which actions are performed.',
      questions: [
        {
          question: 'She runs _____ (quick).',
          answer: 'quickly',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'He speaks _____ (loud).',
          answer: 'loudly',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'They work _____ (hard).',
          answer: 'hard',
          explanation: 'Some adverbs have the same form as their adjectives.'
        },
        {
          question: 'She dances _____ (beautiful).',
          answer: 'beautifully',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'He drives _____ (careful).',
          answer: 'carefully',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'They play _____ (good).',
          answer: 'well',
          explanation: '"Well" is the adverb form of "good".'
        },
        {
          question: 'She sings _____ (sweet).',
          answer: 'sweetly',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'He writes _____ (neat).',
          answer: 'neatly',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'They study _____ (serious).',
          answer: 'seriously',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        },
        {
          question: 'She cooks _____ (wonderful).',
          answer: 'wonderfully',
          explanation: 'Add "-ly" to adjectives to form adverbs of manner.'
        }
      ]
    },
    {
      id: 'p4-topic-8',
      title: 'Conditional Sentences',
      description: 'Learning to use first conditional sentences (if + present, will + base form)',
      purpose: 'To teach students how to form and use first conditional sentences to express possible future situations and their results.',
      questions: [
        {
          question: 'If it rains, I _____ stay home.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If you study hard, you _____ pass the exam.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If she comes early, we _____ go to the park.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If they finish work, they _____ go home.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If I have time, I _____ call you.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If he saves money, he _____ buy a car.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If we leave now, we _____ arrive on time.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If you eat vegetables, you _____ be healthy.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If she practices piano, she _____ improve.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        },
        {
          question: 'If they work together, they _____ succeed.',
          answer: 'will',
          explanation: 'Use "will" in the main clause of first conditional sentences.'
        }
      ]
    },
    {
      id: 'p4-topic-9',
      title: 'Passive Voice',
      description: 'Learning to use passive voice in simple present and past tenses',
      purpose: 'To teach students how to form and use passive voice to emphasize the action or the object rather than the subject.',
      questions: [
        {
          question: 'The letter _____ (write) by John.',
          answer: 'is written',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The house _____ (build) last year.',
          answer: 'was built',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The book _____ (read) by many students.',
          answer: 'is read',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The car _____ (wash) yesterday.',
          answer: 'was washed',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'English _____ (speak) in many countries.',
          answer: 'is spoken',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The movie _____ (watch) by millions of people.',
          answer: 'is watched',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The cake _____ (bake) by my mother.',
          answer: 'was baked',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The letter _____ (send) yesterday.',
          answer: 'was sent',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        },
        {
          question: 'The song _____ (sing) by the choir.',
          answer: 'is sung',
          explanation: 'Use "is/are + past participle" for present passive voice.'
        },
        {
          question: 'The picture _____ (paint) by a famous artist.',
          answer: 'was painted',
          explanation: 'Use "was/were + past participle" for past passive voice.'
        }
      ]
    },
    {
      id: 'p4-topic-10',
      title: 'Reported Speech',
      description: 'Learning to change direct speech to reported speech',
      purpose: 'To teach students how to change direct speech to reported speech by adjusting pronouns, tenses, and time expressions.',
      questions: [
        {
          question: 'Direct: "I am happy." → Reported: He said he _____ happy.',
          answer: 'was',
          explanation: 'Change present tense to past tense in reported speech.'
        },
        {
          question: 'Direct: "I like ice cream." → Reported: She said she _____ ice cream.',
          answer: 'liked',
          explanation: 'Change present tense to past tense in reported speech.'
        },
        {
          question: 'Direct: "I will go to school." → Reported: He said he _____ go to school.',
          answer: 'would',
          explanation: 'Change "will" to "would" in reported speech.'
        },
        {
          question: 'Direct: "I can swim." → Reported: She said she _____ swim.',
          answer: 'could',
          explanation: 'Change "can" to "could" in reported speech.'
        },
        {
          question: 'Direct: "I have finished my homework." → Reported: He said he _____ finished his homework.',
          answer: 'had',
          explanation: 'Change present perfect to past perfect in reported speech.'
        },
        {
          question: 'Direct: "I am studying." → Reported: She said she _____ studying.',
          answer: 'was',
          explanation: 'Change present continuous to past continuous in reported speech.'
        },
        {
          question: 'Direct: "I went to the park." → Reported: He said he _____ to the park.',
          answer: 'had gone',
          explanation: 'Change simple past to past perfect in reported speech.'
        },
        {
          question: 'Direct: "I will help you." → Reported: She said she _____ help me.',
          answer: 'would',
          explanation: 'Change "will" to "would" in reported speech.'
        },
        {
          question: 'Direct: "I must go now." → Reported: He said he _____ go then.',
          answer: 'had to',
          explanation: 'Change "must" to "had to" in reported speech.'
        },
        {
          question: 'Direct: "I am tired." → Reported: She said she _____ tired.',
          answer: 'was',
          explanation: 'Change present tense to past tense in reported speech.'
        }
      ]
    }
  ]
}; 